import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle, XCircle, MinusCircle, AlertCircle } from 'lucide-react';
import { useI18n } from '../../i18n';
import { Button } from '@/components/ui/Button'
import eqStyles from '@/styles/components/compliance/ExtendedQuestionnaire.module.css'

export type ExtendedQuestionnaireBlock = {
  id: string;
  name_en: string;
  name_es: string;
  name_ru: string;
  questions: ExtendedQuestionnaireQuestion[];
};

export type ExtendedQuestionnaireQuestion = {
  id: string;
  text_en: string;
  text_es: string;
  text_ru: string;
  blockId: string;
  weight?: number; // Weight for criticality scoring (default: 1)
};

export type ExtendedQuestionnaireAnswer = 'yes' | 'no' | 'partial' | 'n_a';

export interface ExtendedQuestionnaireProps {
  standardCode: string;
  questionnaireType: 'SELF_ASSESSMENT' | 'DOCUMENTS' | 'PROCESSES' | 'ROLES';
  blocks: ExtendedQuestionnaireBlock[];
  initialAnswers?: Record<string, ExtendedQuestionnaireAnswer>;
  onAnswerChange?: (questionId: string, answer: ExtendedQuestionnaireAnswer) => void;
  onSave?: (data: {
    answers: Record<string, ExtendedQuestionnaireAnswer>;
    blockStats: Record<string, { yes: number; no: number; partial: number; n_a: number }>;
    overallStats: { yes: number; no: number; partial: number; n_a: number; total: number };
  }) => Promise<void>;
  disabled?: boolean;
}

const ExtendedQuestionnaire: React.FC<ExtendedQuestionnaireProps> = ({
  standardCode,
  questionnaireType,
  blocks,
  initialAnswers = {},
  onAnswerChange,
  onSave,
  disabled = false,
}) => {
  const { t, locale } = useI18n();
  const [answers, setAnswers] = useState<Record<string, ExtendedQuestionnaireAnswer>>(initialAnswers);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  const toggleBlock = (blockId: string) => {
    if (disabled) return;
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  };

  const handleAnswerChange = (questionId: string, answer: ExtendedQuestionnaireAnswer) => {
    if (disabled) return;
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    if (onAnswerChange) {
      onAnswerChange(questionId, answer);
    }
  };

  const handleSave = async () => {
    if (disabled || isSaving) return;
    setIsSaving(true);
    try {
      if (onSave) {
        const calculatedBlockStats = calculateBlockStats();
        await onSave({
          answers,
          blockStats: calculatedBlockStats,
          overallStats,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate block statistics for saving (with weight support)
  const calculateBlockStats = () => {
    const stats: Record<string, { yes: number; no: number; partial: number; n_a: number; weightedScore?: number; totalWeight?: number }> = {};
    blocks.forEach((block) => {
      stats[block.id] = { yes: 0, no: 0, partial: 0, n_a: 0, weightedScore: 0, totalWeight: 0 };
      block.questions.forEach((question) => {
        const answer = answers[question.id];
        const weight = question.weight || 1;
        if (answer === 'yes') {
          stats[block.id].yes++;
          stats[block.id].weightedScore! += weight * 1;
        } else if (answer === 'no') {
          stats[block.id].no++;
          stats[block.id].weightedScore! += weight * 0;
        } else if (answer === 'partial') {
          stats[block.id].partial++;
          stats[block.id].weightedScore! += weight * 0.5;
        } else if (answer === 'n_a') {
          stats[block.id].n_a++;
          // N/A doesn't contribute to weighted score
        }
        if (answer !== 'n_a') {
          stats[block.id].totalWeight! += weight;
        }
      });
    });
    return stats;
  };

  const getBlockName = (block: ExtendedQuestionnaireBlock): string => {
    if (locale === 'ru') return block.name_ru;
    if (locale === 'es') return block.name_es;
    return block.name_en;
  };

  const getQuestionText = (question: ExtendedQuestionnaireQuestion): string => {
    if (locale === 'ru') return question.text_ru;
    if (locale === 'es') return question.text_es;
    return question.text_en;
  };

  const blockStats = useMemo(() => {
    const stats: Record<string, { yes: number; no: number; partial: number; n_a: number; weightedScore?: number; totalWeight?: number }> = {};
    blocks.forEach((block) => {
      stats[block.id] = { yes: 0, no: 0, partial: 0, n_a: 0, weightedScore: 0, totalWeight: 0 };
      block.questions.forEach((question) => {
        const answer = answers[question.id];
        const weight = question.weight || 1;
        if (answer === 'yes') {
          stats[block.id].yes++;
          stats[block.id].weightedScore! += weight * 1;
        } else if (answer === 'no') {
          stats[block.id].no++;
          stats[block.id].weightedScore! += weight * 0;
        } else if (answer === 'partial') {
          stats[block.id].partial++;
          stats[block.id].weightedScore! += weight * 0.5;
        } else if (answer === 'n_a') {
          stats[block.id].n_a++;
        }
        if (answer !== 'n_a') {
          stats[block.id].totalWeight! += weight;
        }
      });
    });
    return stats;
  }, [blocks, answers]);

  const overallStats = useMemo(() => {
    let yes = 0;
    let no = 0;
    let partial = 0;
    let n_a = 0;
    Object.values(blockStats).forEach((stat) => {
      yes += stat.yes;
      no += stat.no;
      partial += stat.partial;
      n_a += stat.n_a;
    });
    return { yes, no, partial, n_a, total: yes + no + partial + n_a };
  }, [blockStats]);

  const answerOptions: Array<{ value: ExtendedQuestionnaireAnswer; label: string; icon: React.ReactNode }> = [
    { value: 'yes', label: t('compliance.forms.answers.yes', 'Yes'), icon: <CheckCircle className="w-4 h-4 text-green-600" /> },
    { value: 'no', label: t('compliance.forms.answers.no', 'No'), icon: <XCircle className="w-4 h-4 text-red-600" /> },
    { value: 'partial', label: t('compliance.forms.answers.partial', 'Partial'), icon: <AlertCircle className="w-4 h-4 text-yellow-600" /> },
    { value: 'n_a', label: t('compliance.forms.answers.n_a', 'N/A'), icon: <MinusCircle className="w-4 h-4 text-gray-400" /> },
  ];

  return (
    <div className={eqStyles.root}>
      {/* Overall Statistics */}
      <div className={eqStyles.statsStrip}>
        <div className={eqStyles.statsTitleRow}>
          <h3 className={eqStyles.statsTitle}>
            {t('compliance.forms.stats.overall', 'Overall Statistics')}
          </h3>
          <span className={eqStyles.statsTotal}>
            {t('compliance.forms.stats.total', { count: overallStats.total }, `Total: ${overallStats.total}`)}
          </span>
        </div>
        <div className={eqStyles.statsRow}>
          <div className={eqStyles.statChip}>
            <CheckCircle className="h-4 w-4 shrink-0 text-green-600" aria-hidden />
            <div>
              <div className={`${eqStyles.statValue} text-green-600`}>{overallStats.yes}</div>
              <div className={eqStyles.statLabel}>{t('compliance.forms.stats.yes', 'Yes')}</div>
            </div>
          </div>
          <div className={eqStyles.statChip}>
            <XCircle className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
            <div>
              <div className={`${eqStyles.statValue} text-red-600`}>{overallStats.no}</div>
              <div className={eqStyles.statLabel}>{t('compliance.forms.stats.no', 'No')}</div>
            </div>
          </div>
          <div className={eqStyles.statChip}>
            <AlertCircle className="h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
            <div>
              <div className={`${eqStyles.statValue} text-yellow-600`}>{overallStats.partial}</div>
              <div className={eqStyles.statLabel}>{t('compliance.forms.stats.partial', 'Partial')}</div>
            </div>
          </div>
          <div className={eqStyles.statChip}>
            <MinusCircle className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
            <div>
              <div className={`${eqStyles.statValue} text-gray-400`}>{overallStats.n_a}</div>
              <div className={eqStyles.statLabel}>{t('compliance.forms.stats.n_a', 'N/A')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Blocks */}
      <div className={eqStyles.blocks}>
        {blocks.map((block) => {
          const isExpanded = expandedBlocks.has(block.id);
          const stats = blockStats[block.id];
          const blockAnsweredCount = stats.yes + stats.no + stats.partial + stats.n_a;
          const blockTotalCount = block.questions.length;

          return (
            <div key={block.id} className={eqStyles.block}>
              <button
                type="button"
                onClick={() => toggleBlock(block.id)}
                disabled={disabled}
                className={eqStyles.blockHeader}
              >
                <div className={eqStyles.blockHeaderText}>
                  <p className={eqStyles.blockTitle}>{getBlockName(block)}</p>
                  <div className={eqStyles.blockMeta}>
                    <span>
                      {t('compliance.forms.answered', { answered: blockAnsweredCount, total: blockTotalCount }, `${blockAnsweredCount}/${blockTotalCount} answered`)}
                    </span>
                    {blockAnsweredCount > 0 && (
                      <span className={eqStyles.blockMetaBreakdown}>
                        <span className="text-green-600">✓ {stats.yes}</span>
                        <span className="text-red-600">✗ {stats.no}</span>
                        <span className="text-gray-400">— {stats.n_a}</span>
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`${eqStyles.chevron} ${isExpanded ? eqStyles.chevronOpen : ''}`}
                  aria-hidden
                />
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && !disabled && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className={eqStyles.blockBody}
                  >
                    <div className={eqStyles.blockBodyInner}>
                      {block.questions.map((question) => {
                        const currentAnswer = answers[question.id];

                        return (
                          <div key={question.id} className={eqStyles.questionCard}>
                            <p className={eqStyles.questionText}>
                              {getQuestionText(question)}
                            </p>
                            <div className={eqStyles.answers}>
                              {answerOptions.map((option) => {
                                const isSelected = currentAnswer === option.value;
                                return (
                                  <label
                                    key={option.value}
                                    className={`${eqStyles.answerPill} ${isSelected ? eqStyles.answerPillSelected : ''}`}
                                  >
                                    <input
                                      type="radio"
                                      name={`question-${question.id}`}
                                      value={option.value}
                                      checked={isSelected}
                                      onChange={() => handleAnswerChange(question.id, option.value)}
                                      className="sr-only"
                                      disabled={disabled}
                                    />
                                    {option.icon}
                                    <span>{option.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <div className={eqStyles.saveRow}>
        <Button
          type="button"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={disabled || isSaving}
        >
          {isSaving
            ? t('compliance.forms.saving', 'Saving...')
            : t('compliance.forms.saveResponses', 'Save responses')}
        </Button>
      </div>
    </div>
  );
};

export default ExtendedQuestionnaire;

